import { AlertDialog } from '@base-ui/react/alert-dialog';
import { Field } from '@base-ui/react/field';
import { Fieldset } from '@base-ui/react/fieldset';
import { Plus, Sparkles, X } from 'lucide-react';
import { BooleanField } from '@/components/settings/BooleanField';
import { TextField } from '@/components/settings/TextField';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { HoverTip } from '@/components/ui/HoverTip';
import { Input } from '@/components/ui/Input';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Select } from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import { TextArea } from '@/components/ui/TextArea';
import { AVAILABLE_START_DATE_OPTIONS } from '@/consts/settings';
import {
  stateCodeFromName,
  stateNameFromCode,
  US_STATE_OPTIONS,
} from '@/consts/states';
import { useApplicationAnswersSettings } from '@/providers/settings/SettingsContext';

export interface CustomAnswerRow {
  key: string;
  value: string;
}

export const ApplicationAnswers = () => {
  const {
    answers,
    customAnswerRows,
    employmentHistoryText,
    employmentOverwriteOpen,
    employmentPopulating,
    error,
    onCancelEmploymentOverwrite,
    onChange,
    onConfirmEmploymentOverwrite,
    onCustomAnswerRowsChange,
    onEmploymentHistoryTextChange,
    onPopulateEmployment,
    onReferencesTextChange,
    referencesText,
    resumeAvailable,
  } = useApplicationAnswersSettings();
  const college = answers.education.college ?? {};
  const highSchool = answers.education.highSchool ?? {};
  const selectedStateCode =
    answers.address.stateCode
    || stateCodeFromName(answers.address.state ?? '')
    || null;

  return (
    <>
      <Fieldset.Root>
        <Fieldset.Legend
          render={<SectionHeader>Application answers</SectionHeader>}
        />
        <p className="-mt-1 mb-3 text-xs lg:text-sm text-foreground-muted">
          Used to auto-fill job application forms.
        </p>
        <div className="flex flex-col gap-8 rounded-lg border border-border bg-surface/60 p-12">
          {/* Contact */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Contact
            </p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="First name *"
                onValueChange={(firstName) =>
                  onChange({ ...answers, firstName })
                }
                placeholder="Bob"
                required
                value={answers.firstName}
              />
              <TextField
                label="Last name *"
                onValueChange={(lastName) => onChange({ ...answers, lastName })}
                placeholder="Loblaw"
                required
                value={answers.lastName}
              />
              {/* Hidden field for full name */}
              <TextField
                className="sr-only"
                label="Full name"
                onValueChange={(name) => onChange({ ...answers, name })}
                value={`${answers.firstName} ${answers.lastName}`}
              />
              <TextField
                label="Email *"
                mono
                onValueChange={(email) => onChange({ ...answers, email })}
                placeholder="bobloblaw@bobloblawslawblog.com"
                required
                value={answers.email}
              />
              <TextField
                label="Phone *"
                mono
                onValueChange={(phone) => onChange({ ...answers, phone })}
                pattern="\d*"
                placeholder="6124888888"
                required
                type="number"
                value={answers.phone}
              />
            </div>
          </div>

          {/* Address */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Address
            </p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="Street address *"
                onValueChange={(line1) =>
                  onChange({
                    ...answers,
                    address: { ...answers.address, line1 },
                  })
                }
                required
                value={answers.address.line1}
              />
              <TextField
                label="City *"
                onValueChange={(city) =>
                  onChange({
                    ...answers,
                    address: { ...answers.address, city },
                  })
                }
                required
                value={answers.address.city}
              />
              <Field.Root className="block">
                <div className="mb-1.5">
                  <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                    State *
                  </Field.Label>
                </div>
                <Combobox
                  items={US_STATE_OPTIONS}
                  onValueChange={(stateCode) =>
                    onChange({
                      ...answers,
                      address: {
                        ...answers.address,
                        state: stateCode ? stateNameFromCode(stateCode) : '',
                        stateCode: stateCode ?? '',
                      },
                    })
                  }
                  placeholder="Select a state"
                  required
                  value={selectedStateCode}
                />
              </Field.Root>
              <TextField
                label="Postal code *"
                mono
                onValueChange={(postalCode) =>
                  onChange({
                    ...answers,
                    address: { ...answers.address, postalCode },
                  })
                }
                required
                value={answers.address.postalCode}
              />
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Links
            </p>
            <div className="flex flex-col gap-4">
              <TextField
                label="LinkedIn"
                mono
                onValueChange={(linkedin) =>
                  onChange({
                    ...answers,
                    links: { ...answers.links, linkedin },
                  })
                }
                placeholder="https://linkedin.com/in/…"
                value={answers.links.linkedin}
              />
              <TextField
                label="GitHub"
                mono
                onValueChange={(github) =>
                  onChange({
                    ...answers,
                    links: { ...answers.links, github },
                  })
                }
                placeholder="https://github.com/…"
                value={answers.links.github}
              />
              <TextField
                label="Website"
                mono
                onValueChange={(website) =>
                  onChange({
                    ...answers,
                    links: { ...answers.links, website },
                  })
                }
                placeholder="https://…"
                value={answers.links.website}
              />
            </div>
          </div>

          {/* Eligibility */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Eligibility
            </p>
            <div className="flex flex-col gap-4">
              <BooleanField
                label="Authorized to work in this country *"
                onValueChange={(workAuthorized) =>
                  onChange({ ...answers, workAuthorized })
                }
                required
                value={answers.workAuthorized}
              />
              <BooleanField
                label="Requires visa sponsorship *"
                onValueChange={(needsVisaSponsorship) =>
                  onChange({ ...answers, needsVisaSponsorship })
                }
                required
                value={answers.needsVisaSponsorship}
              />
              <BooleanField
                label="Consents to SMS contact *"
                onValueChange={(smsConsent) =>
                  onChange({ ...answers, smsConsent })
                }
                required
                value={answers.smsConsent}
              />
            </div>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="Desired compensation"
                mono
                onValueChange={(desiredCompensation) =>
                  onChange({ ...answers, desiredCompensation })
                }
                placeholder="$180,000"
                value={answers.desiredCompensation}
              />
              <Field.Root className="block">
                <div className="mb-1.5">
                  <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                    Available to start
                  </Field.Label>
                </div>
                <Select
                  className="w-full"
                  items={AVAILABLE_START_DATE_OPTIONS}
                  onValueChange={(availableStartDateChoice) =>
                    onChange({ ...answers, availableStartDateChoice })
                  }
                  value={answers.availableStartDateChoice}
                />
              </Field.Root>
              <TextField
                label="Years of experience"
                mono
                onValueChange={(yearsExperience) =>
                  onChange({ ...answers, yearsExperience })
                }
                value={answers.yearsExperience}
              />
              <TextField
                label="Date of birth"
                mono
                onValueChange={(dateOfBirth) =>
                  onChange({ ...answers, dateOfBirth })
                }
                placeholder="YYYY-MM-DD"
                value={answers.dateOfBirth}
              />
              <TextField
                label="How heard about us"
                onValueChange={(howHeardAboutUs) =>
                  onChange({ ...answers, howHeardAboutUs })
                }
                value={answers.howHeardAboutUs}
              />
            </div>
          </div>

          {/* EEO */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Equal Opportunity Employment (optional)
            </p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="Gender"
                onValueChange={(gender) =>
                  onChange({ ...answers, eeo: { ...answers.eeo, gender } })
                }
                placeholder="Male"
                value={answers.eeo.gender}
              />
              <TextField
                label="Gender identity"
                onValueChange={(genderIdentity) =>
                  onChange({
                    ...answers,
                    eeo: { ...answers.eeo, genderIdentity },
                  })
                }
                placeholder="Man"
                value={answers.eeo.genderIdentity}
              />
              <TextField
                label="Race"
                onValueChange={(race) =>
                  onChange({ ...answers, eeo: { ...answers.eeo, race } })
                }
                value={answers.eeo.race}
              />
              <TextField
                label="Hispanic or Latino"
                onValueChange={(hispanicLatino) =>
                  onChange({
                    ...answers,
                    eeo: { ...answers.eeo, hispanicLatino },
                  })
                }
                value={answers.eeo.hispanicLatino}
              />
              <TextField
                label="Veteran status"
                onValueChange={(veteranStatus) =>
                  onChange({
                    ...answers,
                    eeo: { ...answers.eeo, veteranStatus },
                  })
                }
                value={answers.eeo.veteranStatus}
              />
              <TextField
                label="Disability status"
                onValueChange={(disabilityStatus) =>
                  onChange({
                    ...answers,
                    eeo: { ...answers.eeo, disabilityStatus },
                  })
                }
                value={answers.eeo.disabilityStatus}
              />
            </div>
          </div>

          {/* Education */}
          <div className="flex flex-col gap-6">
            <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
              Education
            </p>
            <TextField
              label="Highest education *"
              onValueChange={(highestEducation) =>
                onChange({
                  ...answers,
                  education: { ...answers.education, highestEducation },
                })
              }
              placeholder="Bachelor's degree"
              required
              value={answers.education.highestEducation}
            />
            <p className="font-mono text-xs lg:text-sm text-subtle">College</p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="School name"
                onValueChange={(name) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, name },
                    },
                  })
                }
                value={college.name}
              />
              <TextField
                label="City, state"
                onValueChange={(cityState) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, cityState },
                    },
                  })
                }
                value={college.cityState}
              />
              <TextField
                label="Degree"
                onValueChange={(degree) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, degree },
                    },
                  })
                }
                value={college.degree}
              />
              <TextField
                label="Major"
                onValueChange={(major) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, major },
                    },
                  })
                }
                value={college.major}
              />
              <TextField
                label="Years completed"
                mono
                onValueChange={(yearsCompleted) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, yearsCompleted },
                    },
                  })
                }
                value={college.yearsCompleted}
              />
              <BooleanField
                label="Graduated (college)"
                layout="column"
                onValueChange={(graduated) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      college: { ...college, graduated },
                    },
                  })
                }
                required
                value={college.graduated}
              />
            </div>
            <p className="font-mono text-xs lg:text-sm text-subtle">
              High school
            </p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <TextField
                label="School name *"
                onValueChange={(name) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      highSchool: { ...highSchool, name },
                    },
                  })
                }
                placeholder="Big Foot High School"
                required
                value={highSchool.name}
              />
              <TextField
                label="City, state *"
                onValueChange={(cityState) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      highSchool: { ...highSchool, cityState },
                    },
                  })
                }
                placeholder="Walworth, WI"
                required
                value={highSchool.cityState}
              />
              <TextField
                label="Last grade completed *"
                onValueChange={(lastGrade) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      highSchool: { ...highSchool, lastGrade },
                    },
                  })
                }
                placeholder="12"
                required
                value={highSchool.lastGrade}
              />
              <BooleanField
                label="Graduated (high school) *"
                layout="column"
                onValueChange={(graduated) =>
                  onChange({
                    ...answers,
                    education: {
                      ...answers.education,
                      highSchool: { ...highSchool, graduated },
                    },
                  })
                }
                required
                value={highSchool.graduated}
              />
            </div>
          </div>

          {/* Custom answers */}
          <div className="flex flex-col gap-6">
            <div>
              <div className="flex items-baseline justify-between">
                <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
                  Custom answers
                </p>
                <Button
                  onClick={() =>
                    onCustomAnswerRowsChange([
                      ...customAnswerRows,
                      { key: '', value: '' },
                    ])
                  }
                  size="sm"
                  type="button"
                  variant="soft"
                >
                  <Plus className="size-3" /> Add answer
                </Button>
              </div>
              <p className="font-mono text-xs lg:text-sm text-subtle">
                Per-ATS question answers, keyed by field name (e.g.{' '}
                <code className="rounded bg-surface border border-border px-1 py-0.5 text-foreground">
                  workdayPassword
                </code>
                ).
              </p>
            </div>
            {customAnswerRows.length === 0 ? (
              <p className="text-sm lg:text-base text-foreground-muted">
                No custom answers yet.
              </p>
            ) : (
              <div className="space-y-2">
                {customAnswerRows.map(({ key, value }, index) => (
                  <div
                    className="flex items-center gap-2"
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and may share blank keys while editing
                    key={index}
                  >
                    <Input
                      className="w-1/3"
                      font="mono"
                      onChange={(e) =>
                        onCustomAnswerRowsChange(
                          customAnswerRows.map((row, idx) =>
                            idx === index
                              ? { ...row, key: e.target.value }
                              : row,
                          ),
                        )
                      }
                      placeholder="question key"
                      size="sm"
                      value={key}
                    />
                    <Input
                      className="flex-1"
                      onChange={(e) =>
                        onCustomAnswerRowsChange(
                          customAnswerRows.map((row, idx) =>
                            idx === index
                              ? { ...row, value: e.target.value }
                              : row,
                          ),
                        )
                      }
                      placeholder="answer"
                      size="sm"
                      value={value}
                    />
                    <button
                      aria-label="Remove custom answer"
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-foreground-muted transition-colors hover:bg-border/40 hover:text-foreground"
                      onClick={() =>
                        onCustomAnswerRowsChange(
                          customAnswerRows.filter((_, idx) => idx !== index),
                        )
                      }
                      type="button"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Employment history & References */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <p className="font-mono text-sm lg:text-base tracking-wider text-subtle uppercase">
                Employment history &amp; references
              </p>
              <p className="font-mono text-xs lg:text-sm text-subtle">
                JSON arrays of structured entries — these feed
                Workday/Paycor-style histories. Validated on save.
              </p>
            </div>
            <Field.Root className="block">
              <div className="mb-1.5 flex items-center justify-between">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  Employment history (JSON array) *
                </Field.Label>
                <HoverTip
                  disabled={resumeAvailable}
                  text="Upload a resume first"
                >
                  <Button
                    disabled={!resumeAvailable}
                    loading={employmentPopulating}
                    onClick={onPopulateEmployment}
                    size="sm"
                    type="button"
                    variant="primary"
                  >
                    <Sparkles className="size-3" /> Populate from resume
                  </Button>
                </HoverTip>
              </div>
              <TextArea
                className="font-mono text-sm lg:text-sm"
                onChange={(e) => onEmploymentHistoryTextChange(e.target.value)}
                required
                rows={6}
                value={employmentHistoryText}
              />
            </Field.Root>
            <Field.Root className="block">
              <div className="mb-1.5">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  References (JSON array) *
                </Field.Label>
              </div>
              <TextArea
                className="font-mono text-sm lg:text-sm"
                onChange={(e) => onReferencesTextChange(e.target.value)}
                required
                rows={6}
                value={referencesText}
              />
            </Field.Root>
          </div>

          {error && (
            <p className="font-mono text-xs lg:text-sm text-score-low">
              {error}
            </p>
          )}
        </div>
      </Fieldset.Root>
      <AlertDialog.Root
        onOpenChange={(open) => {
          if (!open) onCancelEmploymentOverwrite();
        }}
        open={employmentOverwriteOpen}
      >
        <AlertDialog.Portal>
          <AlertDialog.Backdrop className="fixed inset-0 z-60 bg-black/40 transition-opacity duration-180 data-ending-style:opacity-0 data-starting-style:opacity-0" />
          <AlertDialog.Popup className="fixed top-1/2 left-1/2 z-60 w-80 -translate-1/2 rounded-md border border-border bg-surface shadow-lg">
            <div className="px-5 pt-5 pb-3">
              <AlertDialog.Title className="text-sm lg:text-base font-semibold text-foreground">
                Replace employment history?
              </AlertDialog.Title>
              <AlertDialog.Description className="mt-1 text-sm/relaxed lg:text-base/relaxed text-foreground-muted">
                This will overwrite the current employment history entries with
                ones extracted from your resume.
              </AlertDialog.Description>
            </div>
            <Separator />
            <div className="flex items-center justify-end gap-4 p-3">
              <AlertDialog.Close
                render={
                  <Button
                    onClick={onCancelEmploymentOverwrite}
                    size="sm"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                }
              />
              <Button
                onClick={onConfirmEmploymentOverwrite}
                size="sm"
                variant="primary"
              >
                Replace
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
};
